import { Box, Chip, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import CommonModal from '../../atoms/modal/CommonModal'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import GoogleAddressInput from '../../atoms/input/GoogleAddressInput'
import { updateProfile } from '../../../features/authSlice'
import { ALL_STACKS } from '../../../util/constants'
import { toastHelper } from '../../../util/toastHelper'

const createDraft = (formData = {}) => ({
	businessName: formData.businessName || '',
	businessAddress: formData.businessAddress || '',
	businessTypes: Array.isArray(formData.businessTypes)
		? formData.businessTypes.filter(
				(type) => typeof type === 'string' && type.trim()
			)
		: [],
	customBusinessType: formData.customBusinessType || '',
})

export const BusinessProfileDetailsModal = ({
	open,
	handleClose,
	formData,
	setFormData,
}) => {
	const dispatch = useDispatch()
	const [draft, setDraft] = useState(() => createDraft(formData))
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (open) setDraft(createDraft(formData))
	}, [open, formData])

	const toggleBusinessType = (businessType) => {
		setDraft((current) => {
			const businessTypes = current.businessTypes.includes(businessType)
				? current.businessTypes.filter((type) => type !== businessType)
				: [...current.businessTypes, businessType]
			return { ...current, businessTypes }
		})
	}

	const handleSave = async () => {
		const businessName = draft.businessName.trim()
		const businessAddress = draft.businessAddress.trim()
		const customBusinessType = draft.customBusinessType.trim()
		const businessTypes = [
			...new Set(
				draft.businessTypes.map((type) => type.trim()).filter(Boolean)
			),
		]

		if (!businessName) {
			return toastHelper('Business name is required', 'error')
		}
		if (!businessAddress) {
			return toastHelper('Business address is required', 'error')
		}
		if (!businessTypes.length && !customBusinessType) {
			return toastHelper('Please select a business category', 'error')
		}

		setSaving(true)
		const updates = {
			businessName,
			businessAddress,
			businessTypes,
			customBusinessType,
		}

		try {
			const result = await dispatch(updateProfile(updates))
			if (!result.payload?.success) return

			setFormData((current) => ({
				...current,
				...updates,
				...result.payload.user,
			}))
			handleClose()
		} catch (error) {
			console.error('Error updating business details:', error)
			toastHelper(
				'Could not save business details. Please try again',
				'error'
			)
		} finally {
			setSaving(false)
		}
	}

	return (
		<CommonModal
			open={open}
			handleClose={handleClose}
			heading="Edit business details"
			subHeading="Update the details customers see on your profile"
			footerContent={
				<>
					<ButtonCustom
						variant="outlined"
						onClick={handleClose}
						disabled={saving}
						data-cy="cancel-business-details"
					>
						Cancel
					</ButtonCustom>
					<ButtonCustom
						onClick={handleSave}
						disabled={saving}
						data-cy="save-business-details"
					>
						{saving ? 'Saving...' : 'Save changes'}
					</ButtonCustom>
				</>
			}
		>
			<Box
				component="form"
				onSubmit={(event) => {
					event.preventDefault()
					handleSave()
				}}
				display="flex"
				flexDirection="column"
				gap={2}
				paddingTop={1}
				paddingBottom={2}
			>
				<TextField
					label="Business name"
					value={draft.businessName}
					required
					fullWidth
					inputProps={{ 'data-cy': 'business-profile-name' }}
					onChange={(event) =>
						setDraft((current) => ({
							...current,
							businessName: event.target.value,
						}))
					}
				/>
				<GoogleAddressInput
					data_cy="business-profile-address"
					placeholder="Business address"
					name="businessAddress"
					value={draft.businessAddress}
					setDataFunction={setDraft}
					data={draft}
					required
					showLabel
				/>
				<Box>
					<Typography
						variant="subtitle1"
						sx={{ mb: 1, fontWeight: 600 }}
					>
						Business categories
					</Typography>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
							columnGap: '16px',
							rowGap: '8px',
							width: '100%',
							minWidth: 0,
						}}
					>
						{ALL_STACKS.map((businessType) => {
							const selected =
								draft.businessTypes.includes(businessType)
							return (
								<Chip
									key={businessType}
									disableRipple
									variant="outlined"
									label={businessType}
									sx={{
										width: '100%',
										minWidth: 0,
										boxSizing: 'border-box',
										bgcolor: selected ? '#7ca6bd' : '#FFF',
										color: selected ? '#FFF' : '#7ca6bd',
										borderColor: '#DFE4EB',
										padding: '10px 12px',
										font: 'var(--body-font-three)',
										fontWeight: '400',
									}}
									onClick={() =>
										toggleBusinessType(businessType)
									}
									data-cy={`business-category-${businessType
										.toLowerCase()
										.replace(/[^a-z0-9]+/g, '-')}`}
								/>
							)
						})}
					</Box>
				</Box>
				<TextField
					label="Other category"
					value={draft.customBusinessType}
					fullWidth
					sx={{
						borderRadius: '2em',
						justifyContent: 'start',
						bgcolor: 'white',
						margin: 0,
						'.MuiInputBase-input': {
							padding: '10px 10px 10px 10px',
							fontSize: '14px',
							fontWeight: '400',
							color: '#313131',
						},
						'& .MuiOutlinedInput-root': {
							borderRadius: '2em',
							'& > fieldset': {
								borderColor: '#DFE4EB !important',
							},
						},
					}}
					inputProps={{
						'data-cy': 'business-profile-custom-category',
					}}
					onChange={(event) =>
						setDraft((current) => ({
							...current,
							customBusinessType: event.target.value,
						}))
					}
				/>
			</Box>
		</CommonModal>
	)
}
